import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InventoryItem } from '../../types/inventory';
import { workOrderRecipeService } from '../../services/workOrderRecipeService';
import { roastProfileExistenceKey } from '../../lib/roastProfileQueries';

interface InventoryRoastProfileButtonProps {
  item?: Pick<InventoryItem, 'id' | 'woReferenceId'>;
  referenceId?: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  dataTestId?: string;
  fallbackText?: string; // Text to show if profile doesn't exist (instead of "Add" button)
  readOnly?: boolean;
}

export const InventoryRoastProfileButton: React.FC<InventoryRoastProfileButtonProps> = ({ item, referenceId, onClick, dataTestId, fallbackText, readOnly = false }) => {
  const effectiveRefId = referenceId || item?.id;
  const woRefId = item?.woReferenceId;

  // Held in the shared query cache rather than in component state, so that saving a
  // roast profile can invalidate it and flip this button to "View" while the row stays
  // mounted. As local state it was keyed on ids that never change, so nothing short of
  // a page reload could correct it (INV-021).
  const { data: dto, isLoading: isChecking } = useQuery({
    queryKey: roastProfileExistenceKey({ workOrderId: woRefId, referenceId: effectiveRefId }),
    queryFn: () =>
      woRefId
        ? workOrderRecipeService.getByWorkOrderId(woRefId)
        : workOrderRecipeService.getByReferenceId(effectiveRefId ?? ''),
    enabled: !!(woRefId || effectiveRefId),
  });

  const profileExists = !!dto;

  if (!isChecking && !profileExists && readOnly) {
    return (
      <div className="flex justify-center">
        <span className="text-gray-400 text-sm font-medium italic select-none text-center">N/A</span>
      </div>
    );
  }
  
  if (!isChecking && !profileExists && fallbackText) {
    return (
      <div className="flex justify-center">
        <span className="text-gray-400 text-xs text-center">{fallbackText}</span>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <button
        type="button"
        data-test-id={dataTestId || (item ? `inventory-roast-profile-button-${item.id}` : undefined)}
        onClick={(e) => {
          e.stopPropagation();
          if (!isChecking) {
            onClick(e);
          }
        }}
        className={`
          px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap
          ${isChecking && 'text-gray-400 cursor-not-allowed'}
          ${
            !isChecking &&
            (profileExists
              ? 'bg-primary-500 text-white hover:bg-primary-700'
              : 'bg-gray-300 text-gray-700/50 hover:bg-gray-400')
          }
        `}
      >
        {profileExists ? 'View' : 'Add'}
      </button>
    </div>
  );
}

